import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from '@aqarmap/aqarmap-ui';
import {
  useGetUserQuery,
  useUpdateUserInfoMutation,
} from '~/redux/api/userApiSlice';
import UserType from '~myAccount/pages/AccountDetails/UserType';
import useFormValidations from '~/hooks/useFormValidations';
import AddNewImage from '~/components/AddNewImage';
import ChangeImage from '~/components/ChangeImage';
import ErrorMessage from '~/components/ErrorMessage';
import { UpdatePhotos } from '~myAccount/enums';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import {
  useGetUserProfileQuery,
  useChangeUserNameMutation,
} from '~/redux/api/authApiSlice';
import { objectSerialize } from '~/lib';
import PageLayout from '~myAccount/components/PageLayout';
import { useFullRoutes } from '~/hooks';

const DetailsUpdate = () => {
  const navigate = useNavigate();
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { t } = useTranslation('app');
  const {
    data: response,
    isLoading: userIsLoading,
    isSuccess: userIsSuccess,
  } = useGetUserQuery();

  const {
    data: userProfile,
    isLoading: userProfileIsLoading,
    isSuccess: userProfileIsSuccess,
  } = useGetUserProfileQuery();

  const userPic = response?.user.personal_photo?.thumbnails.large as string;

  const { fullNameSchema } = useFormValidations();
  const [
    updateUserInfo,
    {
      isLoading: updateUserIsLoading,
      isError: updateUserIsError,
      error: updateUserError,
    },
  ] = useUpdateUserInfoMutation();

  const [
    changeUserName,
    {
      isLoading: updateUserNameIsLoading,
      isError: updateUserNameIsError,
      error: updateUserNameError,
    },
  ] = useChangeUserNameMutation();

  const updateFullNameSchema = z.object({ fullName: fullNameSchema });

  type IUpdateFullName = z.infer<typeof updateFullNameSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IUpdateFullName>({
    resolver: zodResolver(updateFullNameSchema),
    mode: 'onChange',
    values: { fullName: userProfile?.name },
  });

  const onSubmit = async (data: IUpdateFullName) => {
    if (imageFile) {
      const formData = objectSerialize({
        'user_update_profile[personalPhoto][file]': imageFile as File,
        fullName: data.fullName || userProfile?.name,
      });
      const updateUserImageRes: any = await updateUserInfo(formData);
      const changeUserImage =
        updateUserImageRes.data && !updateUserImageRes.error;

      if (changeUserImage) {
        navigate(MY_ACCOUNT_URL.ROOT);
      }
    }

    if (data.fullName) {
      const formData = objectSerialize({
        fullName: data.fullName,
      });
      const updateUserNameRes: any = await changeUserName(formData);
      const isSuccess = updateUserNameRes.data && !updateUserNameRes.error;
      if (isSuccess) {
        navigate(MY_ACCOUNT_URL.ROOT);
      }
    }
  };
  const errorMessageUserImage = (updateUserError as any)?.data?.message;
  const errorMessageUserName = (updateUserNameError as any)?.data?.message;
  const usrePic = response?.user.personal_photo?.thumbnails.large;

  const isError = updateUserIsError || updateUserNameIsError;
  const isLoading = userIsLoading || userProfileIsLoading;
  const isSuccess = userIsSuccess || userProfileIsSuccess;
  return (
    <PageLayout
      title={t('accountDetails')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      {isLoading && <LayoutSkeleton />}
      {isError && (
        <ErrorMessage message={errorMessageUserImage || errorMessageUserName} />
      )}

      {!userIsLoading && isSuccess && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3x">
            {userPic ? (
              <ChangeImage
                title={t('accountImage')}
                type={UpdatePhotos.PERSONAL_PHOTO}
                imgSrc={userPic}
                getImageFile={(file) => setImageFile(file)}
              />
            ) : (
              <AddNewImage
                title={t('accountImage')}
                getImageFile={(file) => setImageFile(file)}
              />
            )}
            {usrePic && !response.user.is_valid_personal_photo && (
              <p className="text-body_2 mt-x w-full bg-orange_light_2 text-orange_dark_2 font-normal p-1.5x rounded-[8px] mb-3x">
                {t('app:imageStillProcessing')}
              </p>
            )}
            <Controller
              control={control}
              name="fullName"
              render={({ field }) => (
                <Input
                  field={{
                    ...field,
                    placeholder: t('fullNamePlaceholder'),
                    autoFocus: true,
                    id: 'fullName',
                  }}
                  error={errors[field.name]?.message as string}
                  label={{
                    text: t('fullName'),
                    className: 'font-[600] block',
                  }}
                />
              )}
            />
            <UserType />
          </div>
          <div className="flex items-center px-3x pb-3x gap-2x border-t border-gray pt-2x">
            <Button
              size="medium"
              ariaLabel="submit-btn"
              type="submit"
              variant="primary"
              isLoading={updateUserIsLoading || updateUserNameIsLoading}
              disabled={isLoading}
              className=" w-auto "
            >
              {t('save')}
            </Button>

            <Button
              size="medium"
              ariaLabel="cancel-btn"
              type="button"
              variant="secondary"
              className="w-auto"
              onClick={() => navigate(-1)}
            >
              {t('cancel')}
            </Button>
          </div>
        </form>
      )}
    </PageLayout>
  );
};

export default DetailsUpdate;
